import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { useLang } from "@/i18n/LanguageContext";

export default function Pricing({ onSelectPlan }) {
  const { t } = useLang();
  const [yearly, setYearly] = useState(false);
  const p = t.pricing;
  const price = yearly ? p.main.priceY : p.main.priceM;

  return (
    <section id="preise" className="mx-auto max-w-6xl px-5 sm:px-8 py-24 sm:py-36" data-testid="pricing-section">
      <Reveal className="text-center">
        <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
          <Star size={11} /> {p.kicker}
        </p>
        <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="pricing-title">
          {p.title}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base md:text-lg" style={{ color: "var(--muted-warm)" }}>{p.sub}</p>

        <div className="mt-9 inline-flex items-center rounded-full border p-1" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid="billing-toggle">
          <button
            data-testid="billing-monthly-btn"
            onClick={() => setYearly(false)}
            className="rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: !yearly ? "var(--camel)" : "transparent", color: !yearly ? "#fff" : "var(--on-surface)" }}
          >
            {p.monthly}
          </button>
          <button
            data-testid="billing-yearly-btn"
            onClick={() => setYearly(true)}
            className="flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: yearly ? "var(--camel)" : "transparent", color: yearly ? "#fff" : "var(--on-surface)" }}
          >
            {p.yearly}
            <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "var(--gold)", color: "#fff" }}>
              {p.save}
            </span>
          </button>
        </div>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
        {/* Main card */}
        <Reveal className="relative flex flex-col rounded-3xl border-2 p-8 sm:p-10 lg:col-span-2 shadow-[0_40px_80px_-40px_rgba(212,175,55,0.5)]" style={{ background: "var(--surface-secondary)", borderColor: "var(--gold)" }} data-testid="pricing-card-main">
          <span className="absolute -top-3.5 left-8 flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] font-bold uppercase text-white" style={{ background: "var(--gold)", letterSpacing: "0.1em" }}>
            <Star size={10} color="#fff" /> {p.main.name}
          </span>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={price}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="font-serif-display text-6xl font-semibold"
                    data-testid="pricing-price-main"
                  >
                    {price}
                  </motion.span>
                </AnimatePresence>
                <span className="text-sm" style={{ color: "var(--muted-warm)" }}>{p.perMonth}</span>
              </div>
              <div className="mt-2 h-7">
                {!yearly && (
                  <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: "var(--rose-soft)", color: "var(--camel)" }} data-testid="pricing-annual-hint">
                    ✦ {p.youSave}: {p.main.priceY}{p.perMonth} ({p.save})
                  </span>
                )}
              </div>
            </div>
          </div>
          <ul className="mt-6 grid flex-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            {p.main.features.map((f, j) => (
              <li key={j} className="flex items-start gap-2.5 text-sm leading-relaxed">
                <span className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }}>✦</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            data-testid="pricing-cta-main"
            onClick={() => onSelectPlan(p.main.name)}
            className="pill-btn mt-9 rounded-full py-4 text-sm font-semibold"
            style={{ background: "var(--camel)", color: "#fff" }}
          >
            {p.main.cta}
          </button>
          <p className="mt-3 text-center text-xs" style={{ color: "var(--muted-warm)" }} data-testid="pricing-main-note">{p.main.note}</p>
        </Reveal>

        {/* Enterprise card (subtle) */}
        <Reveal delay={0.1} className="flex flex-col rounded-3xl border p-8" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid="pricing-card-enterprise">
          <h3 className="text-sm font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--muted-warm)" }}>{p.enterprise.name}</h3>
          <p className="mt-3 font-serif-display text-xl font-semibold leading-snug">{p.enterprise.tag}</p>
          <ul className="mt-6 flex-1 space-y-3.5">
            {p.enterprise.features.map((f, j) => (
              <li key={j} className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>
                <span className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }}>✦</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <a
            href="mailto:info@mira-mirror.ai"
            data-testid="pricing-cta-enterprise"
            className="pill-btn mt-9 rounded-full py-3.5 text-center text-sm font-medium"
            style={{ background: "var(--surface)", color: "var(--camel)", border: "1px solid var(--camel)" }}
          >
            {p.enterprise.cta}
          </a>
          <p className="mt-3 text-center text-xs" style={{ color: "var(--muted-warm)" }}>info@mira-mirror.ai</p>
        </Reveal>
      </div>

      {/* Growth block */}
      <Reveal delay={0.12} className="mt-8">
        <div className="rounded-3xl border p-7 sm:flex sm:items-start sm:gap-6" style={{ borderColor: "var(--gold)", background: "var(--rose-soft)" }} data-testid="pricing-growth-block">
          <p className="shrink-0 font-serif-display text-lg font-semibold sm:max-w-[220px]">{p.growth.title}</p>
          <p className="mt-2 text-sm leading-relaxed sm:mt-0" style={{ color: "var(--muted-warm)" }} data-testid="pricing-growth-text">{p.growth.text}</p>
        </div>
      </Reveal>

      {/* Hardware block */}
      <Reveal delay={0.15} className="mt-8">
        <div className="rounded-3xl border p-7 sm:flex sm:items-start sm:gap-6" style={{ borderColor: "var(--line)", background: "var(--surface)" }} data-testid="pricing-hardware-block">
          <p className="shrink-0 font-serif-display text-lg font-semibold sm:max-w-[220px]">{p.hardware.title}</p>
          <p className="mt-2 text-sm leading-relaxed sm:mt-0" style={{ color: "var(--muted-warm)" }}>{p.hardware.text}</p>
        </div>
      </Reveal>

      <Reveal delay={0.2} className="mt-10 text-center">
        <p className="text-sm font-medium" style={{ color: "var(--camel)" }} data-testid="pricing-b2c-note">
          ✦ {p.b2c}
        </p>
        <p className="mt-4 text-xs" style={{ color: "var(--muted-warm)" }} data-testid="pricing-footnote">
          {p.footnote}
        </p>
      </Reveal>
    </section>
  );
}
